import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Container,
  Paper,
  Grid,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import SEOHead from "../components/SEOHead";
import CitabilitySection from "../components/CitabilitySection";
import {
  School as SchoolIcon,
  Business as BusinessIcon,
} from "@mui/icons-material";

interface Partner {
  id: string;
  name: string;
  logo: string;
  category: "school" | "organization" | "corporate";
  description?: string;
}

const Partners: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  // Partner data - actual partners
  const partners: Partner[] = [
    // Schools
    {
      id: "chcc-lee-shiu-keung",
      name: "中華傳道會李賢堯紀念中學",
      logo: "/images/partners/中華傳道會李賢堯紀念中學.png",
      category: "school",
      description: "到校課程合作",
    },
    {
      id: "pui-ying",
      name: "培英中學",
      logo: "/images/partners/培英中學.png",
      category: "school",
      description: "到校課程合作",
    },
    {
      id: "nam-wah-catholic",
      name: "天主教南華中學",
      logo: "/images/partners/天主教南華中學.png",
      category: "school",
      description: "到校課程合作",
    },
    {
      id: "ningbo-no2",
      name: "寧波第二中學",
      logo: "/images/partners/寧波第二中學.png",
      category: "school",
      description: "到校課程合作",
    },
    {
      id: "mu-kuang",
      name: "慕光英文書院",
      logo: "/images/partners/慕光英文書院.png",
      category: "school",
      description: "到校課程合作",
    },
    {
      id: "salvation-army-william-booth",
      name: "救世軍卜維廉中學",
      logo: "/images/partners/救世軍卜維廉中學.png",
      category: "school",
      description: "到校課程合作",
    },
    {
      id: "tsang-pik-shan",
      name: "曾璧山中學",
      logo: "/images/partners/曾璧山中學.png",
      category: "school",
      description: "到校課程合作",
    },
    {
      id: "hung-hom-lutheran",
      name: "紅磡信義學校",
      logo: "/images/partners/紅磡信義學校.png",
      category: "school",
      description: "到校課程合作",
    },
    {
      id: "ymca-college",
      name: "青年會書院",
      logo: "/images/partners/青年會書院.png",
      category: "school",
      description: "到校課程合作",
    },
    {
      id: "kings-college",
      name: "英皇書院",
      logo: "/images/partners/英皇書院.png",
      category: "school",
      description: "到校課程合作",
    },
    // Organizations
    {
      id: "cfsc",
      name: "基督教家庭服務中心",
      logo: "/images/partners/基督教家庭服務中心.png",
      category: "organization",
      description: "社區手工藝活動",
    },
    {
      id: "neighbourhood-advice",
      name: "鄰舍輔導會",
      logo: "/images/partners/鄰舍輔導會.png",
      category: "organization",
      description: "社區手工藝活動",
    },
    {
      id: "bgca-hk",
      name: "香港小童群益會",
      logo: "/images/partners/香港小童群益會.png",
      category: "organization",
      description: "社區手工藝活動",
    },
  ];

  const categories = [
    {
      id: "school",
      title: "合作學校",
      icon: <SchoolIcon sx={{ fontSize: 32 }} />,
      color: "#4c9f4c",
      bgColor: "#e8f5e8",
    },
    {
      id: "organization",
      title: "合作機構",
      icon: <BusinessIcon sx={{ fontSize: 32 }} />,
      color: "#9c27b0",
      bgColor: "#f3e5f5",
    },
  ];

  const getPartnersByCategory = (category: string) => {
    return partners.filter((p) => p.category === category);
  };

  const baseUrl = "https://cosylabdesigns.com";
  const structuredData = useMemo(
    () => [
      {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        name: "CosyLab",
        url: baseUrl,
        logo: `${baseUrl}/icon/icon.png`,
        image: `${baseUrl}/icon/icon.png`,
        areaServed: { "@type": "City", name: "Hong Kong" },
        address: {
          "@type": "PostalAddress",
          addressLocality: "Hong Kong",
          addressRegion: "Hong Kong",
          addressCountry: "HK",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "首頁",
            item: `${baseUrl}/`,
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "合作機構",
            item: `${baseUrl}/partners`,
          },
        ],
      },
    ],
    []
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `
          linear-gradient(135deg, #ffffff 0%, #f8f9fa 25%, #f1f3f4 50%, #e8f5e8 75%, #f3e5f5 100%),
          radial-gradient(circle at 20% 80%, rgba(76, 175, 80, 0.03) 0%, transparent 50%),
          radial-gradient(circle at 80% 20%, rgba(156, 39, 176, 0.03) 0%, transparent 50%)
        `,
        py: { xs: 6, md: 10 },
      }}
    >
      <SEOHead
        title="合作機構 | CosyLab- 一站式到校課程合作夥伴"
        description="CosyLab合作機構：與多間香港學校、教育機構提供一站式到校課程服務。包括中小學、社區中心等合作夥伴，提供專業手工藝興趣班與工作坊。"
        keywords="合作機構, 合作學校, 到校課程合作, 學校合作夥伴, 一站式到校課程, 興趣班合作, 工作坊合作, 香港學校合作"
        canonical="https://cosylabdesigns.com/partners"
        structuredData={structuredData}
      />

      <Container maxWidth="lg">
        {/* Hero Section */}
        <Box
          sx={{
            textAlign: "center",
            mb: { xs: 6, md: 10 },
          }}
        >
          <Typography
            variant="h2"
            component="h1"
            sx={{
              fontFamily: "'Inter', sans-serif",
              fontWeight: 800,
              fontSize: { xs: "2.5rem", md: "4rem" },
              lineHeight: 1.1,
              background: "linear-gradient(135deg, #4c9f4c 0%, #9c27b0 50%, #f59e0b 100%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              mb: 3,
              letterSpacing: "-0.02em",
            }}
          >
            合作機構
          </Typography>

          <Typography
            variant="h5"
            sx={{
              fontFamily: "'Inter', sans-serif",
              fontWeight: 400,
              color: "#64748b",
              maxWidth: 700,
              mx: "auto",
              lineHeight: 1.6,
              mb: 2,
              fontSize: "1.25rem",
            }}
          >
            感謝各合作夥伴的信任與支持
          </Typography>

          <Typography
            variant="body1"
            sx={{
              fontFamily: "'Inter', sans-serif",
              color: "#94a3b8",
              maxWidth: 600,
              mx: "auto",
              lineHeight: 1.6,
            }}
          >
            我們與多間學校及機構建立合作關係，提供專業的一站式到校課程服務
          </Typography>
        </Box>

        
        {/* Partners by Category */}
        {categories.map((category) => {
          const categoryPartners = getPartnersByCategory(category.id);
          if (categoryPartners.length === 0) return null;

          return (
            <Box key={category.id} sx={{ mb: { xs: 8, md: 12 } }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  mb: 4,
                }}
              >
                <Box
                  sx={{
                    width: 50,
                    height: 50,
                    borderRadius: "50%",
                    backgroundColor: category.bgColor,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: category.color,
                  }}
                >
                  {category.icon}
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontFamily: "'Inter', sans-serif",
                    fontWeight: 700,
                    color: "#2d3748",
                  }}
                >
                  {category.title}
                </Typography>
              </Box>

              <Grid container spacing={3}>
                {categoryPartners.map((partner) => (
                  <Grid item xs={6} sm={4} md={3} key={partner.id}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 3,
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "white",
                        border: "1px solid #e2e8f0",
                        borderRadius: 3,
                        cursor: "pointer",
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        position: "relative",
                        overflow: "hidden",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
                          borderColor: category.color,
                          "& .partner-logo": {
                            transform: "scale(1.05)",
                          },
                        },
                      }}
                      onMouseEnter={() => setHoveredId(partner.id)}
                      onMouseLeave={() => setHoveredId(null)}
                    >
                      <Box
                        component="img"
                        className="partner-logo"
                        src={partner.logo}
                        alt={partner.name}
                        loading="lazy"
                        sx={{
                          width: "100%",
                          height: "auto",
                          maxHeight: 80,
                          objectFit: "contain",
                          transition: "all 0.3s ease",
                          mb: 2,
                        }}
                        onError={(e) => {
                          // Fallback if image doesn't exist
                          const target = e.target as HTMLImageElement;
                          target.style.display = "none";
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "'Inter', sans-serif",
                          fontWeight: 600,
                          color: "#2d3748",
                          textAlign: "center",
                          fontSize: "0.9rem",
                        }}
                      >
                        {partner.name}
                      </Typography>

                      {partner.description && (
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: "'Inter', sans-serif",
                            fontWeight: 500,
                            color: "#64748b",
                            textAlign: "center",
                            fontSize: "0.82rem",
                            mt: 0.5,
                            lineHeight: 1.35,
                          }}
                        >
                          {partner.description}
                        </Typography>
                      )}

                      {hoveredId === partner.id && partner.description && (
                        <Box
                          sx={{
                            position: "absolute",
                            bottom: 0,
                            left: 0,
                            right: 0,
                            background: `linear-gradient(transparent, ${category.color}f0)`,
                            p: 2,
                            color: "white",
                            fontSize: "0.75rem",
                            textAlign: "center",
                          }}
                        >
                          {partner.description}
                        </Box>
                      )}
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          );
        })}

        {/* Empty State - if no partners yet */}
        {partners.length === 0 && (
          <Paper
            elevation={0}
            sx={{
              p: { xs: 6, md: 10 },
              textAlign: "center",
              background: "white",
              border: "1px solid #e2e8f0",
              borderRadius: 4,
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: "#64748b",
                mb: 2,
              }}
            >
              合作機構資料準備中
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "#94a3b8",
              }}
            >
              我們正在整理合作夥伴資料，敬請期待
            </Typography>
          </Paper>
        )}

        {/* Call to Action */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 6, md: 8 },
            mt: { xs: 8, md: 12 },
            background: "linear-gradient(135deg, #f8fafc 0%, #ffffff 50%, #f1f5f9 100%)",
            border: "1px solid #e2e8f0",
            borderRadius: 4,
            textAlign: "center",
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontFamily: "'Inter', sans-serif",
              fontWeight: 700,
              mb: 3,
              fontSize: { xs: "1.8rem", md: "2.2rem" },
              color: "#2d3748",
            }}
          >
            成為我們的合作夥伴
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontFamily: "'Inter', sans-serif",
              color: "#64748b",
              maxWidth: 600,
              mx: "auto",
              lineHeight: 1.6,
              mb: 4,
              fontSize: "1.1rem",
            }}
          >
            我們提供專業的一站式到校課程服務，包括課程設計、導師安排、材料準備等完整方案。
            歡迎學校及機構聯絡我們，共同為學生創造優質的學習體驗。
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontFamily: "'Inter', sans-serif",
                fontWeight: 600,
                color: "#4c9f4c",
              }}
            >
              查詢合作：
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontFamily: "'Inter', sans-serif",
                color: "#64748b",
              }}
            >
              WhatsApp: +852 8437 8839
            </Typography>
          </Box>
        </Paper>
      </Container>

    </Box>
  );
};

export default Partners;
